// "Raise work" from a record page (Session ASM, gap W-12 in docs/reports/OFFICE-UI-RESTRUCTURE-AUDIT-2026-09-23.md).
//
// One control for the lift, site, customer and defect pages, offering whichever of three EXISTING flows makes sense
// from that page, each opened already knowing the customer, site and lift the page is about. It is never a second
// form: every option lands in the flow's own normal entry point.
//
//   - "Log a call-out" goes to the Call-outs page's ordinary Log call-out form (/office/callouts?new=log, the deep link
//     the dashboard, the Call log and the Enquiry queue already use), with the customer, and where known the site and
//     the lift, chosen.
//   - "Log a planned repair" opens the ordinary Raise work form (RaiseWorkModal) on "Planned repair", with the lift
//     chosen (or, with no lift, the customer, whose lifts the picker narrows to). From a DEFECT it carries the defect's
//     id, and the server links the repair to the defect exactly as the automatic remedial path does (remedialLink.ts).
//   - "Raise a SAFed test" opens the SAFed raise form (Session BDC): the same form the lift page's SAFed panel uses, with the lift
//     chosen (or the lift list narrowed to the site or customer). SAFed is its own job type, so it sits beside the others.
//   - "Start a quote" opens the ordinary quote calculator (/office/quotes/new) with the customer and site pre-filled
//     and linked, the navigation shape the LOLER report and the Call log already use.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { RaiseWorkModal } from "./RaiseWorkModal";
import { RaiseSafedTestModal } from "./RaiseSafedTestModal";
import { Button } from "../../components/Button";

export type RaiseWorkOption = "callout" | "repair" | "safed" | "quote";

export interface RaiseWorkContext {
  customerId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  siteId?: string | null;
  siteName?: string | null;
  siteAddress?: string | null;
  assetId?: string | null;
  defectId?: string | null;
  // The defect's own words, used as the repair's starting description.
  defectDescription?: string | null;
}

const LABELS: Record<RaiseWorkOption, string> = {
  callout: "Log a call-out",
  repair: "Log a planned repair",
  safed: "Raise a SAFed test",
  quote: "Start a quote",
};

// Where "Log a call-out" goes, exactly as the Call-outs page reads it (CalloutsPage.tsx). Pure, so it is tested.
export function calloutLink(ctx: RaiseWorkContext): string {
  const q = new URLSearchParams({ new: "log" });
  if (ctx.customerId) {
    q.set("customerId", ctx.customerId);
    if (ctx.siteId) {
      q.set("siteId", ctx.siteId);
      if (ctx.assetId) q.set("assetId", ctx.assetId);
    }
  }
  return `/office/callouts?${q.toString()}`;
}

// The quote calculator's navigation state (QuoteCalculatorPage.tsx navState), as the LOLER report sends it. Pure.
export function quoteNavigationState(ctx: RaiseWorkContext): {
  prefill: { customerName: string; customerEmail: string; siteName: string; siteAddress: string };
  link: { customerId: string | null; siteId: string | null };
} {
  return {
    prefill: {
      customerName: ctx.customerName ?? "",
      customerEmail: ctx.customerEmail ?? "",
      siteName: ctx.siteName ?? "",
      siteAddress: ctx.siteAddress ?? "",
    },
    link: { customerId: ctx.customerId ?? null, siteId: ctx.siteId ?? null },
  };
}

export function RaiseWorkMenu({
  context,
  options,
  onRaised,
}: {
  context: RaiseWorkContext;
  options: RaiseWorkOption[];
  // Called once a planned repair was actually raised, so the host page can reload (a defect page shows its new link).
  onRaised?: () => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  const [safedOpen, setSafedOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement | null>(null);

  // Close the menu on an outside click or Escape, the office's usual menu behaviour.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function choose(option: RaiseWorkOption) {
    setOpen(false);
    if (option === "callout") navigate(calloutLink(context));
    else if (option === "quote") navigate("/office/quotes/new", { state: quoteNavigationState(context) });
    else if (option === "safed") setSafedOpen(true);
    else setRepairOpen(true);
  }

  if (options.length === 0) return null;
  return (
    <div ref={wrap} className="relative inline-block">
      <Button variant="secondary" size="sm" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Raise work
        <ChevronDown aria-hidden size={14} />
      </Button>
      {open && (
        <ul role="menu" aria-label="Raise work" className="absolute right-0 z-20 mt-1 min-w-[12rem] rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] py-1 shadow-lg">
          {options.map((option) => (
            <li key={option} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => choose(option)}
                className="block w-full px-3 py-2 text-left text-sm text-ele-strong hover:bg-ele-purple/20"
              >
                {LABELS[option]}
              </button>
            </li>
          ))}
        </ul>
      )}
      {notice && (
        <p role="status" className="mt-1 text-xs text-ele-green">
          {notice}
        </p>
      )}
      {safedOpen && (
        <RaiseSafedTestModal
          assetId={context.assetId}
          customerId={context.customerId}
          siteId={context.siteId}
          onClose={() => setSafedOpen(false)}
          onRaised={() => {
            setSafedOpen(false);
            setNotice("SAFed test job raised. Book the engineers and the day on the Scheduling page.");
            onRaised?.();
          }}
        />
      )}
      {repairOpen && (
        <RaiseWorkModal
          preset={{
            type: "repair",
            ...(context.assetId ? { assetId: context.assetId } : {}),
            ...(context.customerId ? { customerId: context.customerId } : {}),
            ...(context.customerName ? { customerName: context.customerName } : {}),
            ...(context.defectId ? { defectId: context.defectId } : {}),
            ...(context.defectDescription ? { description: context.defectDescription } : {}),
          }}
          onClose={() => setRepairOpen(false)}
          onRaised={(result) => {
            setRepairOpen(false);
            setNotice(result.reused ? "Added to the open job already booked for this lift." : "Work raised. It is on the Needs scheduling panel on the Scheduling page.");
            onRaised?.();
          }}
        />
      )}
    </div>
  );
}
